import { useState, useEffect, useCallback, useMemo } from 'react';
import { previousFriday } from 'date-fns';
import { toLocalDateStr } from '../lib/utils';
import { BrowserMondayService } from '@/services/monday-service';
import type { MondayActivityEvent, MondayBoard } from '@/services/types';
import { PlatformIndicators, normalizeMondayEvents } from './PlatformIndicators';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft,
  faBolt,
  faCalendarDays,
  faChevronDown,
  faChevronRight,
  faCircleExclamation,
  faEyeSlash,
  faFilter,
  faMagnifyingGlass,
  faPlus,
  faRotate,
  faSpinner,
  faUsers,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

const mkIcon = (def: import('@fortawesome/fontawesome-svg-core').IconDefinition) =>
  ({ className = '' }: { className?: string }) =>
    <FontAwesomeIcon icon={def} className={className} />;

interface MondayActivityViewProps {
  onClose: () => void;
  apiKey: string;
  inline?: boolean;
}

const EVENT_TYPE_CONFIG: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; label: string }> = {
  update: { icon: mkIcon(faArrowRightArrowLeft), color: 'text-blue-400', label: 'Update' },
  create_pulse: { icon: mkIcon(faPlus), color: 'text-emerald-400', label: 'Created' },
  move_pulse: { icon: mkIcon(faArrowRightArrowLeft), color: 'text-cyan-400', label: 'Moved' },
  change_column_value: { icon: mkIcon(faBolt), color: 'text-yellow-400', label: 'Column Change' },
  assign: { icon: mkIcon(faUsers), color: 'text-purple-400', label: 'Assignment' },
};

export function MondayActivityView({ onClose, apiKey, inline }: MondayActivityViewProps) {
  const [events, setEvents] = useState<MondayActivityEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [boards, setBoards] = useState<MondayBoard[]>([]);
  const [selectedBoard, setSelectedBoard] = useState('');
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [lookbackDays, setLookbackDays] = useState(7);

  // Filters
  const [filterSearch, setFilterSearch] = useState('');
  const [filterAuthor, setFilterAuthor] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  const service = useMemo(() => new BrowserMondayService({ apiKey }), [apiKey]);

  useEffect(() => {
    loadBoards();
  }, []);

  const loadBoards = async () => {
    setLoadingBoards(true);
    try {
      const b = await service.getBoards();
      setBoards(b);
      if (b.length > 0) setSelectedBoard(b[0].id);
    } catch (err: any) {
      console.error('Failed to load Monday boards:', err);
    } finally {
      setLoadingBoards(false);
    }
  };

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await service.getRecentActivity(selectedBoard || undefined, lookbackDays);
      setEvents(data);
      const today = toLocalDateStr();
      const yesterday = toLocalDateStr(new Date(Date.now() - 86400000));
      setExpandedDates(new Set([today, yesterday]));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch Monday activity');
    } finally {
      setLoading(false);
    }
  }, [selectedBoard, lookbackDays, service]);

  useEffect(() => {
    fetchActivity();
  }, [selectedBoard, fetchActivity]);

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      if (filterAuthor && e.author.displayName !== filterAuthor) return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        if (!e.itemName.toLowerCase().includes(q) &&
            !e.boardName.toLowerCase().includes(q) &&
            !e.author.displayName.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [events, filterAuthor, filterSearch]);

  const groupedByDate = useMemo(() => {
    const groups = new Map<string, MondayActivityEvent[]>();
    for (const event of filteredEvents) {
      const date = toLocalDateStr(new Date(event.timestamp));
      if (!groups.has(date)) groups.set(date, []);
      groups.get(date)!.push(event);
    }
    return groups;
  }, [filteredEvents]);

  const uniqueAuthors = useMemo(() => {
    const set = new Set(events.map((e) => e.author.displayName));
    return [...set].sort();
  }, [events]);

  const toggleDate = (date: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  return (
    <div className={`${inline ? 'min-h-full' : 'fixed inset-0 z-50'} bg-white dark:bg-slate-950 text-gray-900 dark:text-white overflow-hidden flex flex-col`}>
      {/* Header - hidden when inline */}
      {!inline && (
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                <FontAwesomeIcon icon={faXmark} className="h-5 w-5 text-gray-500 dark:text-slate-400" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-pink-600/20 flex items-center justify-center">
                  <img src="/brands/monday/dark.svg" className="w-7 h-auto" alt="" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900 dark:text-white">Monday.com Activity</h1>
                  <p className="text-xs text-gray-400 dark:text-slate-500">{filteredEvents.length} events</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <select
                value={selectedBoard}
                onChange={(e) => setSelectedBoard(e.target.value)}
                disabled={loadingBoards}
                className="px-3 py-2 bg-gray-100 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500/30"
              >
                <option value="">All boards</option>
                {boards.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>

              <select
                value={lookbackDays}
                onChange={(e) => setLookbackDays(Number(e.target.value))}
                className="px-3 py-2 bg-gray-100 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500/30"
              >
                <option value={(() => {
                  const now = new Date();
                  return Math.ceil((now.getTime() - previousFriday(now).getTime()) / (1000 * 60 * 60 * 24));
                })()}>Friday -&gt; Monday (now)</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>

              <button
                onClick={fetchActivity}
                disabled={loading}
                className="px-4 py-2 bg-pink-600 hover:bg-pink-500 disabled:opacity-50 rounded-lg text-sm font-medium text-white flex items-center gap-2 transition-colors"
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> : <FontAwesomeIcon icon={faRotate} className="h-4 w-4" />}
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/50 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-xs">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                type="text"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="Search items..."
                className="w-full pl-9 pr-3 py-2 bg-gray-100 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500/30"
              />
            </div>
            <select
              value={filterAuthor}
              onChange={(e) => setFilterAuthor(e.target.value)}
              className="px-3 py-2 bg-gray-100 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none"
            >
              <option value="">All authors</option>
              {uniqueAuthors.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <button onClick={() => setShowFilters(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg">
              <FontAwesomeIcon icon={faEyeSlash} className="h-4 w-4 text-slate-500" />
            </button>
          </div>
        </div>
      )}

      {!showFilters && (
        <div className="flex-shrink-0 px-6 py-1.5 border-b border-gray-200 dark:border-slate-800">
          <button onClick={() => setShowFilters(true)} className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1">
            <FontAwesomeIcon icon={faFilter} className="h-3 w-3" /> Show filters
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="mb-4 flex items-start gap-2.5 p-3 bg-white/50 dark:bg-white/[0.04] backdrop-blur-md border border-black/[0.06] dark:border-white/[0.08] rounded-xl">
            <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4 text-indigo-500 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-slate-700 dark:text-slate-200">Beta Feature</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Monday.com integration is in beta and may be prone to errors or incomplete data.</p>
            </div>
          </div>
          {error && (
            <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg flex items-center gap-3">
              <FontAwesomeIcon icon={faCircleExclamation} className="h-5 w-5 text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {loading && events.length === 0 && (
            <div className="flex items-center justify-center py-20">
              <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 animate-spin text-pink-400" />
            </div>
          )}

          {!loading && events.length === 0 && !error && (
            <div className="text-center py-20 text-slate-500">
              <FontAwesomeIcon icon={faCalendarDays} className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No activity found. Select a board and refresh.</p>
            </div>
          )}

          {/* Lead & Lag Indicators */}
          {events.length > 0 && (
            <div className="mb-4">
              <PlatformIndicators events={normalizeMondayEvents(events)} platform="monday" />
            </div>
          )}

          {[...groupedByDate.entries()].map(([date, dateEvents]) => {
            const isExpanded = expandedDates.has(date);
            const isToday = date === toLocalDateStr();
            const isYesterday = date === toLocalDateStr(new Date(Date.now() - 86400000));
            const label = isToday ? 'Today' : isYesterday ? 'Yesterday' : new Date(date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

            return (
              <div key={date} className="mb-2">
                <button
                  onClick={() => toggleDate(date)}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-white/60 dark:bg-slate-800/50 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg transition-colors border border-gray-200 dark:border-transparent"
                >
                  {isExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 text-gray-400 dark:text-slate-400" /> : <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 text-gray-400 dark:text-slate-400" />}
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{label}</span>
                  <span className="text-xs text-slate-500">{dateEvents.length} events</span>
                </button>

                {isExpanded && (
                  <div className="ml-4 border-l-2 border-gray-200 dark:border-slate-800 pl-4 py-2 space-y-2">
                    {dateEvents.map((event) => {
                      const config = EVENT_TYPE_CONFIG[event.type] || { icon: mkIcon(faBolt), color: 'text-slate-400', label: event.type };
                      const Icon = config.icon;

                      return (
                        <div key={event.id} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-slate-900/50 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800/50 transition-colors">
                          <div className={`mt-0.5 ${config.color}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-pink-400 bg-pink-500/10 px-1.5 py-0.5 rounded">{event.boardName}</span>
                              <span className="text-sm text-white truncate">{event.itemName}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                              <span>{event.author.displayName}</span>
                              <span>•</span>
                              <span>{new Date(event.timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>
                              {event.fieldName && (
                                <>
                                  <span>•</span>
                                  <span className="text-slate-500">{event.fieldName}</span>
                                </>
                              )}
                              {event.fromValue && event.toValue && (
                                <>
                                  <span className="text-slate-600">{event.fromValue}</span>
                                  <span>→</span>
                                  <span className="text-pink-300">{event.toValue}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
